// Mixpanel wrapper — thin layer over mixpanel-browser so the rest of the app
// never imports the SDK directly. Initializes once at app startup (App.js);
// every other call site just imports { track, identify, reset, setProfile }.
//
// Token comes from REACT_APP_MIXPANEL_TOKEN (frontend/.env locally, a GitHub
// Actions secret in production — see .github/workflows/netlify-deploy.yml).
// Mixpanel project tokens are write-only and meant to ship in client bundles,
// unlike the Supabase service-role-style secrets elsewhere in this app.
import mixpanel from "mixpanel-browser";

const TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;

let ready = false;

// There's only one Mixpanel project (one token), so local dev, Netlify deploy
// previews, and the real live site all write into the same data set unless
// tagged. NODE_ENV isn't a reliable signal for this — `npm run build` run
// locally for a quick smoke test still sets NODE_ENV=production — so this
// checks where the code is actually running instead.
function detectEnvironment() {
  if (typeof window === "undefined") return "unknown";
  const host = window.location.hostname;
  if (host === "ahpickles.in" || host === "www.ahpickles.in") return "production";
  if (host === "localhost" || host === "127.0.0.1") return "local";
  if (host.startsWith("staging--")) return "staging";
  return "preview"; // e.g. a Netlify deploy-preview URL
}

// mixpanel.reset() (called on sign-out) wipes ALL registered super
// properties, not just identity — so platform/environment would silently
// disappear from every event for the rest of that browser session unless
// re-registered immediately after. Centralized here so init and reset can't
// drift out of sync with each other.
function registerBaseProperties() {
  mixpanel.register({ platform: "web", environment: detectEnvironment(), user_type: "anonymous", username: "guest" });
}

export function initAnalytics() {
  if (ready || !TOKEN) return;
  mixpanel.init(TOKEN, {
    debug: process.env.NODE_ENV !== "production",
    track_pageview: false, // handled manually below (fires on exit, with duration — see App.js)
    persistence: "localStorage",
    ignore_dnt: false,
    // Autocapture (click/scroll/submit/rage-click/dead-click) is off —
    // every event sent to Mixpanel is now an explicit, deliberately named
    // track() call (business actions, nav clicks, etc.), so the live event
    // feed never shows an unreadable generic "[Auto] Element Click" row.
    autocapture: false,
  });
  registerBaseProperties();
  ready = true;
}

// Lets event-level reports segment by who did the action without joining to
// a profile — set once per session as soon as the auth state resolves
// (AuthContext.jsx). "anonymous" covers visitors who never sign in.
export function registerUserType(userType) {
  if (!ready) return;
  mixpanel.register({ user_type: userType });
}

// Puts a human-readable name on literally every event so anyone reading
// the live Mixpanel feed can see who did something without clicking into
// a People profile. Falls back to "guest" for anonymous visitors —
// registerBaseProperties() sets that default at init/reset; this overrides
// it once a signed-in profile resolves.
export function registerUsername(name) {
  if (!ready) return;
  mixpanel.register({ username: name || "guest" });
}

// Same reasoning as registerUsername — but unlike username there's no
// "guest" fallback to display, since anonymous visitors have no email/phone
// at all. Only called for signed-in profiles that actually have these on
// file; omitted entirely otherwise rather than sending an empty string.
// mixpanel.reset() (sign-out) wipes these along with every other super
// property, same as everything else registered outside registerBaseProperties.
export function registerContactInfo(email, phone) {
  if (!ready) return;
  const props = {};
  if (email) props.email = email;
  if (phone) props.phone = phone;
  if (Object.keys(props).length) mixpanel.register(props);
}

// Every call below is a safe no-op until initAnalytics() has run (e.g. if
// the token isn't configured in this environment) — callers never need to
// guard their own track() calls.

// Drop null/undefined/"" property values rather than send them — Mixpanel
// best practice is to omit a property entirely when it doesn't apply.
function clean(properties) {
  return properties
    ? Object.fromEntries(Object.entries(properties).filter(([, v]) => v !== null && v !== undefined && v !== ""))
    : undefined;
}

export function track(eventName, properties) {
  if (!ready) return;
  mixpanel.track(eventName, clean(properties));
}

// Header/footer nav links have no business outcome of their own (unlike
// product_added_to_cart etc.) — they'd otherwise only show up as generic
// autocapture "[Auto] Element Click" rows with no indication of which link
// was clicked. This gives them a plain-English event name instead, e.g.
// "Clicked on Products", so the live event feed reads clearly without
// opening each row's properties. `label` is always the canonical English
// link name (not the translated UI text) so the same link produces the same
// event name regardless of site language.
export function trackNavClick(label, source) {
  track(`Clicked on ${label}`, { source });
}

export function identify(userId) {
  if (!ready || !userId) return;
  mixpanel.identify(userId);
}

export function setProfile(attributes) {
  if (!ready) return;
  mixpanel.people.set(clean(attributes));
}

export function setProfileOnce(attributes) {
  if (!ready) return;
  mixpanel.people.set_once(clean(attributes));
}

export function reset() {
  if (!ready) return;
  mixpanel.reset();
  registerBaseProperties();
}
